<script setup lang="ts">
import { PopoverArrow, PopoverContent, PopoverPortal, PopoverRoot, PopoverTrigger } from "reka-ui";
import { computed, ref } from "vue";

import type { Column } from "@/models/Column";

interface Props {
  columns: Array<Pick<Column, "fieldPath" | "displayName">>;
  hidden: string[];
}

withDefaults(defineProps<Props>(), {  })
const emit = defineEmits<(e: "onStateChange", hidden: string[]) => void>();

const open = ref(false);
const sortedColumns = computed(() => {
  return [...__props.columns].sort((a, b) => a.displayName.localeCompare(b.displayName));
});

function setOpen(state: boolean) {
  open.value = state;
}

function toggleColumn(state: string | boolean) {
  let newState = [...__props.hidden];

  if (typeof state === "boolean") {
    newState = state ? [] : __props.columns.map((col) => col.fieldPath);
  } else if (__props.hidden.includes(state)) {
    newState = __props.hidden.filter((column) => column !== state);
  } else newState.push(state);

  emit("onStateChange", newState);
}
</script>

<template>
  <PopoverRoot :modal="true" :open="open" @update:open="setOpen">
    <PopoverTrigger :aria-label="open ? 'Hide Columns Selection' : 'Select Columns'" class="tw-btn-lg-gray">
      {{ open ? "Hide Columns Selection" : "Select Columns" }}
    </PopoverTrigger>
    <PopoverPortal>
      <PopoverContent side="bottom" align="start" :as-child="true" :align-offset="3">
        <ul
          class="tw-b-r tw-flex tw-max-h-[calc(var(--reka-popover-content-available-height)-1rem)] tw-max-w-xs tw-flex-col tw-overflow-y-auto tw-rounded-lg tw-bg-white tw-p-2 tw-shadow"
        >
          <li class="tw-flex tw-min-h-0 tw-shrink-0 tw-gap-2 tw-py-1">
            <input
              id="toggle_selectAll"
              type="checkbox"
              class="tw-shrink-0"
              :checked="hidden.length === 0"
              @change="toggleColumn(($event.target as HTMLInputElement).checked)"
            />
            <label for="toggle_selectAll" class="tw-flex-1 tw-cursor-pointer tw-select-none tw-text-sm">
              Select all
            </label>
          </li>
          <li
            v-for="column in sortedColumns"
            :key="column.fieldPath"
            class="tw-flex tw-min-h-0 tw-shrink-0 tw-gap-2 tw-py-1"
          >
            <input
              :id="`toggle_${column.fieldPath}`"
              type="checkbox"
              class="tw-shrink-0"
              :checked="!hidden.includes(column.fieldPath)"
              :aria-label="`Toggle to ${hidden.includes(column.fieldPath) ? 'Show' : 'Hide'} ${
                column.displayName
              } Column`"
              @change="() => toggleColumn(column.fieldPath)"
            />
            <label :for="`toggle_${column.fieldPath}`" class="tw-flex-1 tw-cursor-pointer tw-select-none tw-text-sm">
              {{ column.displayName }}
            </label>
          </li>
        </ul>
        <PopoverArrow class="tw-fill-white" />
      </PopoverContent>
    </PopoverPortal>
  </PopoverRoot>
</template>
